/**
 * Discovery data + UI hooks.
 *
 * Extracted from app/components/v2/discovery/discovery-page.tsx so the page is a
 * declarative shell. The three tab hooks (classes / instructors / studios) are
 * intentionally symmetric: each owns its own search input, filter selection, and
 * filter handlers, so the page wires every tab identically.
 *
 * The exported key-builders are the SINGLE source of truth for query keys — the
 * route loader (app/routes/_authenticated/discovery.tsx) imports them too, so the
 * SSR prefetch + client hydration can never drift apart (the #1 refactor risk).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { useDelayedFlag } from '@/hooks/ui/useDelayedFlag';
import { v2 } from '@/lib/i18n/v2';
import { toSGTCivilKey } from '@/lib/utils/date';
import { APP_ROUTES } from '@/constants/routes';
import { createStudiosMapHistoryEntry } from '@/lib/navigation/studiosMapHistory';
import type { FilterSection } from '@/components/v2/discovery/filters';
import {
  getClasses,
  getDiscoveryFiltersQueryFn,
  getInstructors,
  getStudios,
  type ClassTimeGrouped,
  type FilterPill,
  type InstructorV2,
  type LevelFilter,
  type StudioFilter,
  type StudioV2,
} from '@/lib/api/v2/discovery';

export type DiscoveryTab = 'classes' | 'instructors' | 'studios';

// ---------------------------------------------------------------------------
// Query key builders — shared with the route loader (SSR prefetch).
// Default landing state MUST reproduce these exactly or the prefetch misses on
// hydration. The classes key is versioned to discard persisted cursors whose
// ordering contract predates the frozen pagination snapshot.
// ---------------------------------------------------------------------------

export const discoveryFiltersKey = () => ['discovery-filters'] as const;

export const discoveryClassesKey = (
  dateStart: string,
  dateEnd: string,
  filters: string[],
  showFavourites: boolean,
  search: string
) => ['discovery-classes', 'v2', dateStart, dateEnd, filters, showFavourites, search] as const;

export const discoveryInstructorsKey = (
  search: string,
  showFavourites: boolean,
  filters: string[]
) => ['discovery-instructors', search, showFavourites, filters] as const;

export const discoveryStudiosKey = (search: string, showFavourites: boolean, filters: string[]) =>
  ['discovery-studios', search, showFavourites, filters] as const;

// ---------------------------------------------------------------------------
// Filter options (levels / genres / studios) + filter-pill view models
// ---------------------------------------------------------------------------

export function useDiscoveryFilterOptions() {
  const {
    data: filtersData,
    isError,
    refetch,
  } = useQuery({
    queryKey: discoveryFiltersKey(),
    queryFn: getDiscoveryFiltersQueryFn,
  });

  const filterSections = useMemo<{
    levels: FilterSection;
    genres: FilterSection;
    studios: FilterSection;
  }>(() => {
    if (!filtersData) {
      return {
        levels: { id: 'levels', title: v2.discoveryPage.filters.level, options: [] },
        genres: { id: 'genres', title: v2.discoveryPage.filters.genre, options: [] },
        studios: { id: 'studios', title: v2.discoveryPage.filters.studio, options: [] },
      };
    }
    return {
      levels: {
        id: 'levels',
        title: v2.discoveryPage.filters.level,
        options: filtersData.levels.map((l: LevelFilter) => ({
          id: `level-${l.id}`,
          name: l.name,
        })),
      },
      genres: {
        id: 'genres',
        title: v2.discoveryPage.filters.genre,
        options: filtersData.genres.map((g: string) => ({ id: `genre-${g}`, name: g })),
      },
      studios: {
        id: 'studios',
        title: v2.discoveryPage.filters.studio,
        options: filtersData.studios.map((s: StudioFilter) => ({
          id: `studio-${s.id}`,
          name: s.name,
        })),
      },
    };
  }, [filtersData]);

  /** Resolve raw `level-/genre-/studio-` filter ids into display pills. */
  const buildFilterPills = useCallback(
    (filterIds: string[]): FilterPill[] => {
      if (!filtersData) return [];
      return filterIds.map((filterId) => {
        const [type, ...rest] = filterId.split('-');
        const idPart = rest.join('-');
        let name = idPart;
        if (type === 'level') {
          const level = filtersData.levels.find((l) => l.id === Number(idPart));
          if (level) name = level.name;
        } else if (type === 'genre') {
          name = idPart;
        } else if (type === 'studio') {
          const studio = filtersData.studios.find((s) => s.id === idPart);
          if (studio) name = studio.name;
        }
        return { type: type as FilterPill['type'], id: filterId, name };
      });
    },
    [filtersData]
  );

  return {
    filtersData,
    filterSections,
    buildFilterPills,
    filtersError: isError,
    refetchFilters: refetch,
  };
}

// Shared filter-selection handlers (identical shape across the three tabs).
function useFilterSelection(initialFilters: string[] = []) {
  const [filters, setFilters] = useState<string[]>(initialFilters);
  const toggleFilter = useCallback((filterId: string) => {
    setFilters((prev) =>
      prev.includes(filterId) ? prev.filter((f) => f !== filterId) : [...prev, filterId]
    );
  }, []);
  const removeFilter = useCallback(
    (filter: FilterPill) => setFilters((prev) => prev.filter((f) => f !== filter.id)),
    []
  );
  const clearFilters = useCallback(() => setFilters([]), []);
  // Whole-selection commit, used by the filter sheet's Apply (the sheet edits a
  // local draft and commits once, instead of one refetch per chip tap).
  const applyFilters = useCallback((next: string[]) => setFilters(next), []);
  return { filters, setFilters, toggleFilter, removeFilter, clearFilters, applyFilters };
}

// URL-seeded studio filter (#891). The ?studio= deep-link param seeds local
// filter state; the URL is the single source of truth, so every action that
// drops the seed (pill remove, clear-all) must drop the param too, and when
// the param disappears (tab switch / back nav) the local seed must go as well.
// Otherwise a refresh after clearing re-seeds the removed filter.
function useStudioSeededFilterSelection(studioParam?: string) {
  const navigate = useNavigate();
  const seededFilter = studioParam ? `studio-${studioParam}` : undefined;
  const selection = useFilterSelection(seededFilter ? [seededFilter] : []);
  // Tracks the seed that last synced local state so a disappearing param can
  // remove exactly that filter without touching user-added filters.
  const previousSeed = useRef<string | undefined>(seededFilter);

  useEffect(() => {
    const seed = seededFilter;
    const previous = previousSeed.current;
    if (seed) {
      selection.setFilters([seed]);
    } else if (previous) {
      // Capture the seed in a local: the functional updater runs after this
      // effect body, when previousSeed.current has already been reset.
      selection.setFilters((prev) => prev.filter((f) => f !== previous));
    }
    previousSeed.current = seed;
  }, [seededFilter]);

  const clearStudioParam = useCallback(() => {
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, studio: undefined }),
    });
  }, [navigate]);

  const toggleFilter = useCallback(
    (filterId: string) => {
      // The seed is in local state whenever the param exists, so toggling the
      // seeded studio in the bottom sheet is always a toggle-OFF: drop the
      // param too, or a refresh would resurrect the filter (#891).
      if (seededFilter && filterId === seededFilter) clearStudioParam();
      selection.toggleFilter(filterId);
    },
    [seededFilter, clearStudioParam, selection.toggleFilter]
  );

  const removeFilter = useCallback(
    (filter: FilterPill) => {
      if (seededFilter && filter.id === seededFilter) clearStudioParam();
      selection.removeFilter(filter);
    },
    [seededFilter, clearStudioParam, selection.removeFilter]
  );

  const clearFilters = useCallback(() => {
    if (seededFilter) clearStudioParam();
    selection.clearFilters();
  }, [seededFilter, clearStudioParam, selection.clearFilters]);

  const applyFilters = useCallback(
    (next: string[]) => {
      // The seed is in local state whenever the param exists, so applying a
      // selection that drops the seeded studio is always a seed-drop: clear the
      // param too, or a refresh would resurrect the filter (#891).
      if (seededFilter && !next.includes(seededFilter)) clearStudioParam();
      selection.setFilters(next);
    },
    [seededFilter, clearStudioParam, selection.setFilters]
  );

  return { ...selection, toggleFilter, removeFilter, clearFilters, applyFilters };
}

// ---------------------------------------------------------------------------
// Classes — prefetched by the loader (default key hits cache on mount).
// ---------------------------------------------------------------------------

export function useDiscoveryClasses({
  selectedDate,
  showFavourites,
  enabled,
  studioParam,
}: {
  selectedDate: Date;
  showFavourites: boolean;
  enabled: boolean;
  studioParam?: string;
}) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  // Deep-link: pre-filter classes by studio when arriving from a studio detail
  // page's "See class schedule" CTA; keeps URL and visible filter in sync (#891).
  const selection = useStudioSeededFilterSelection(studioParam);

  const { dateStart, dateEnd } = useMemo(() => {
    // SGT civil key (YYYY-MM-DD) — must match the SSR loader's key so the
    // hydrated page hits the prefetched query on any device timezone (#874).
    const dateStr = toSGTCivilKey(selectedDate);
    return { dateStart: dateStr, dateEnd: dateStr };
  }, [selectedDate]);

  const query = useInfiniteQuery({
    queryKey: discoveryClassesKey(
      dateStart,
      dateEnd,
      selection.filters,
      showFavourites,
      debouncedSearch
    ),
    queryFn: ({ pageParam }) =>
      getClasses(dateStart, dateEnd, {
        levels: selection.filters.filter((f) => f.startsWith('level-')),
        genres: selection.filters.filter((f) => f.startsWith('genre-')),
        studios: selection.filters.filter((f) => f.startsWith('studio-')),
        cursor: pageParam,
        search: debouncedSearch || undefined,
        favourites: showFavourites || undefined,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore && lastPage.pagination?.cursor
        ? lastPage.pagination.cursor
        : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    // Day/filter changes keep the previous params' list on screen (the delayed
    // skeleton covers first loads) instead of blanking to nothing mid-fetch.
    placeholderData: keepPreviousData,
  });

  // Preserve backend time grouping when flattening for the selected date.
  // While the query serves placeholder data (previous params' pages) the
  // flatten is suppressed: those pages are keyed by the PREVIOUS day, and
  // rendering them under the newly selected day would show wrong rows.
  const classes = useMemo(() => {
    if (query.isPlaceholderData || !query.data?.pages) return [];
    const selectedDateStr = toSGTCivilKey(selectedDate);
    const result: {
      id: string;
      name: string;
      level?: string;
      startTime: string;
      endTime: string | null;
      instructorId: string;
      instructor: string;
      studioId: string;
      studioName: string;
    }[] = [];
    query.data.pages.forEach((page) => {
      if (!page?.data) return;
      const dateClasses = (page.data as Record<string, Record<string, ClassTimeGrouped[]>>)[
        selectedDateStr
      ];
      if (!dateClasses) return;
      Object.entries(dateClasses as Record<string, ClassTimeGrouped[]>).forEach(
        ([, timeSlots]: [string, ClassTimeGrouped[]]) => {
          timeSlots.forEach((cls: ClassTimeGrouped) => {
            result.push({
              id: cls.id,
              name: cls.name,
              level: cls.level,
              startTime: cls.startTime,
              endTime: cls.endTime,
              instructorId: cls.instructor?.id || '',
              instructor: cls.instructor?.name || '',
              studioId: cls.studio.id,
              studioName: cls.studio.name,
            });
          });
        }
      );
    });
    return result;
  }, [query.data, query.isPlaceholderData, selectedDate]);

  const asOf = query.data?.pages[0]?.pagination?.asOf;
  // Placeholder data counts as "still loading" for the skeleton: the previous
  // params' payload must never paint as the new selection's result.
  const skel = useDelayedFlag(query.isLoading || query.isPlaceholderData);
  // Empty only once the fetch has settled — gating on isFetching closes the
  // flash window before the delayed skeleton activates.
  const isEmpty =
    !query.isLoading && !query.isFetching && !query.isPlaceholderData && classes.length === 0;
  const hasMore = Boolean(query.hasNextPage) && classes.length > 0;

  return {
    classes,
    asOf,
    skel,
    isEmpty,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isFetchNextPageError: query.isFetchNextPageError,
    refetch: query.refetch,
    isFetchingNextPage: query.isFetchingNextPage,
    hasMore,
    fetchNextPage: query.fetchNextPage,
    search,
    setSearch,
    ...selection,
  };
}

// ---------------------------------------------------------------------------
// Instructors — CSR only (not prefetched). Owns the ?studio= deep-link effect.
// ---------------------------------------------------------------------------

export function useDiscoveryInstructors({
  showFavourites,
  enabled,
  studioParam,
}: {
  showFavourites: boolean;
  enabled: boolean;
  studioParam?: string;
}) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  // Deep-link: pre-filter instructors by studio when arriving from a studio
  // detail page; keeps URL and visible filter in sync (#891).
  const selection = useStudioSeededFilterSelection(studioParam);

  const query = useInfiniteQuery({
    queryKey: discoveryInstructorsKey(debouncedSearch, showFavourites, selection.filters),
    queryFn: ({ pageParam }) =>
      getInstructors({
        search: debouncedSearch || undefined,
        studios: selection.filters
          .filter((f) => f.startsWith('studio-'))
          .map((f) => f.slice('studio-'.length)),
        genres: selection.filters
          .filter((f) => f.startsWith('genre-'))
          .map((f) => f.slice('genre-'.length)),
        favourites: showFavourites || undefined,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore && lastPage.pagination?.cursor
        ? lastPage.pagination.cursor
        : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    // Filter/search changes keep the previous params' rows visible (react-query
    // marks them isPlaceholderData) instead of blanking the tab mid-fetch.
    placeholderData: keepPreviousData,
  });

  const instructors = useMemo((): InstructorV2[] => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page?.instructors ?? []);
  }, [query.data]);

  const skel = useDelayedFlag(query.isLoading);

  return {
    instructors,
    skel,
    // Settled-empty for the empty-state gate: raw length === 0 flashes "No
    // results" during the first fetch / a filter change.
    isEmpty:
      !query.isLoading && !query.isFetching && !query.isPlaceholderData && instructors.length === 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    isFetchNextPageError: query.isFetchNextPageError,
    refetch: query.refetch,
    isFetchingNextPage: query.isFetchingNextPage,
    // Placeholder pages' cursor belongs to the previous params — never auto-
    // fetch "next" off it; the observer re-arms once real data lands.
    hasMore: Boolean(query.hasNextPage) && !query.isPlaceholderData,
    fetchNextPage: query.fetchNextPage,
    search,
    setSearch,
    ...selection,
  };
}

// ---------------------------------------------------------------------------
// Studios — prefetched by the loader; tab-gated like classes/instructors so it
// doesn't fetch on non-studio tabs. The loader's prefetched default key means
// switching to the tab is an instant cache hit (no loading flash).
// ---------------------------------------------------------------------------

export function useDiscoveryStudios({
  showFavourites,
  enabled,
}: {
  showFavourites: boolean;
  enabled: boolean;
}) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const selection = useFilterSelection();

  const query = useInfiniteQuery({
    queryKey: discoveryStudiosKey(debouncedSearch, showFavourites, selection.filters),
    queryFn: async ({ pageParam }) => {
      const result = await getStudios({
        cursor: pageParam,
        search: debouncedSearch || undefined,
        genres: selection.filters
          .filter((f) => f.startsWith('genre-'))
          .map((f) => f.slice('genre-'.length)),
        favourites: showFavourites || undefined,
      });
      return result;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasMore ? lastPage.pagination.cursor : undefined,
    enabled,
    // Filter/search changes keep the previous params' rows visible instead of
    // blanking the tab mid-fetch.
    placeholderData: keepPreviousData,
  });

  const studios = useMemo((): StudioV2[] => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page?.studios ?? []);
  }, [query.data]);

  const skel = useDelayedFlag(query.isLoading);

  return {
    studios,
    skel,
    // Settled-empty for the empty-state gate: raw length === 0 flashes "No
    // results" during the first fetch / a filter change.
    isEmpty:
      !query.isLoading && !query.isFetching && !query.isPlaceholderData && studios.length === 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    isFetchNextPageError: query.isFetchNextPageError,
    refetch: query.refetch,
    isFetchingNextPage: query.isFetchingNextPage,
    // Placeholder pages' cursor belongs to the previous params — never auto-
    // fetch "next" off it; the observer re-arms once real data lands.
    hasMore: Boolean(query.hasNextPage) && !query.isPlaceholderData,
    fetchNextPage: query.fetchNextPage,
    search,
    setSearch,
    ...selection,
  };
}

// ---------------------------------------------------------------------------
// Navigation — tab switch, card clicks, favourites toggle.
// ---------------------------------------------------------------------------

export function useDiscoveryNavigation({
  showFavourites,
  studioParam,
}: {
  showFavourites: boolean;
  studioParam?: string;
}) {
  const navigate = useNavigate();

  const handleTabChange = useCallback(
    (tab: string) => {
      navigate({
        to: '/discovery',
        search: {
          tab: tab as DiscoveryTab,
          studio: undefined,
          favourites: showFavourites ? 'true' : undefined,
        },
      });
    },
    [navigate, showFavourites]
  );

  const handleToggleFavourites = useCallback(() => {
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, favourites: !showFavourites ? 'true' : undefined }),
    });
  }, [showFavourites, navigate]);

  const handleStudiosMapClick = useCallback(
    () =>
      navigate({
        to: APP_ROUTES.DISCOVERY_STUDIOS_MAP,
        state: (previous) => ({
          ...previous,
          til: {
            ...previous.til,
            studiosMap: createStudiosMapHistoryEntry({
              tab: 'studios',
              favourites: showFavourites ? 'true' : undefined,
              studio: studioParam,
            }),
          },
        }),
      }),
    [navigate, showFavourites, studioParam]
  );

  // Detail-entry navigation (class/instructor/studio) lives in the cards
  // themselves as <Link> elements now, so `defaultPreload: 'intent'` warms the
  // detail chunk + loader on hover/touch (perf audit part 2, Theme 2) — no
  // navigate callbacks here anymore.

  return {
    handleTabChange,
    handleToggleFavourites,
    handleStudiosMapClick,
  };
}

// ---------------------------------------------------------------------------
// Per-tab UI state — search-bar visibility + filter-sheet visibility, unified
// as per-tab Records (previously a Record for search + three loose bools).
// Only the active tab renders its sheet/search, so a single active-tab view is
// all the page needs.
// ---------------------------------------------------------------------------

export function useTabUiState(activeTab: DiscoveryTab) {
  const [searchByTab, setSearchByTab] = useState<Record<DiscoveryTab, boolean>>({
    classes: false,
    instructors: false,
    studios: false,
  });
  const [sheetByTab, setSheetByTab] = useState<Record<DiscoveryTab, boolean>>({
    classes: false,
    instructors: false,
    studios: false,
  });

  const toggleSearch = useCallback(() => {
    setSearchByTab((prev) => ({ ...prev, [activeTab]: !prev[activeTab] }));
  }, [activeTab]);
  const openSheet = useCallback(() => {
    setSheetByTab((prev) => ({ ...prev, [activeTab]: true }));
  }, [activeTab]);
  const closeSheet = useCallback(() => {
    setSheetByTab((prev) => ({ ...prev, [activeTab]: false }));
  }, [activeTab]);

  return {
    showSearch: searchByTab[activeTab],
    toggleSearch,
    sheetOpen: sheetByTab[activeTab],
    openSheet,
    closeSheet,
  };
}
